import { createSelector } from '@reduxjs/toolkit';

import type { EmptyFiltrationState } from './types';
import { name } from './slices';

import { activeFiltersSelector } from '../../selectors/filters';
import {
  isAwaitingFiltration,
  isSearchCompleteSelector,
} from '../../searchStatus/selectors';

export const emptyFiltrationSelector = (state): EmptyFiltrationState =>
  state.get(name);

export const isEmptyFiltrationSelector = createSelector(
  [emptyFiltrationSelector],
  (state) => state.isEmptyFiltration,
);

/**
 * Находятся ли penalizedHotels в зоне видимости (viewport)
 */
export const isVisiblePenalizedHotelsSelector = createSelector(
  [emptyFiltrationSelector],
  (state) => state.isVisiblePenalizedHotels,
);

/**
 * Проверяет, есть ли отфильтрованные отели (filtered_hotels)
 * @deprecated это временный селектор, чтобы поправить неконсистентный ответ с фильтром по отелю
 * @see https://tracker.yandex.ru/LT-33996
 */
export const hasFilteredHotelsSelector = createSelector(
  [emptyFiltrationSelector],
  (state) => state.hasFilteredHotels,
);

/**
 * Состояние, если поиск закончен и под фильтры не попал ни один отель
 */
export const isEmptySearchWithFiltersSelector = createSelector(
  [isSearchCompleteSelector, isAwaitingFiltration, hasFilteredHotelsSelector],
  (isComplete, awaitingFiltration, filteredHotels) => {
    const result = isComplete && !awaitingFiltration && filteredHotels;
    return Boolean(result);
  },
);

/**
 * Были ли показаны penalizedHotels хотя бы раз
 */
export const isShownPenalizedHotelsSelector = createSelector(
  [emptyFiltrationSelector],
  (state) => state.showPenalizedHotels,
);

/**
 * Список отелей, которые частично не попали под фильтры
 */
export const penalizedHotelsSelector = createSelector(
  [emptyFiltrationSelector, activeFiltersSelector],
  (state, activeFilters) => {
    const hotels = state.penalizedHotels;

    /**
     * Дописываем названия фильтров (они хранятся на фронте)
     */
    const hotelsWithPenalizedFilerts = hotels.map((hotel) => ({
      ...hotel,
      penalized_filters: hotel.penalized_filters
        ? activeFilters.filter((filter) =>
            hotel.penalized_filters?.some(
              (filterName) => filter.filterName === filterName,
            ),
          )
        : [],
    }));

    return hotelsWithPenalizedFilerts;
  },
);

export const penalizedHotelsCountSelector = createSelector(
  [penalizedHotelsSelector],
  (penalizedHotels) => penalizedHotels.length || 0,
);

export const penalizedHotelsVisibleLimitSelector = createSelector(
  [emptyFiltrationSelector],
  (slice) => slice.visibleLimit,
);

export const isVisibleShowMorePenalizedHotelsSelector = createSelector(
  [penalizedHotelsCountSelector, penalizedHotelsVisibleLimitSelector],
  (hotelsLength, visibleLimit) => hotelsLength > visibleLimit,
);
